import { useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Sparkles, Send, Trash2, RefreshCw, AlertTriangle, Wallet, HandCoins, Factory, Gauge, Cpu, ChevronDown, RotateCw, Paperclip, Camera, X, FileText, Loader2 } from "lucide-react";

const wrap = "ti-dark -m-3 min-h-[calc(100vh-3rem)] w-full max-w-full overflow-x-hidden px-4 py-4 pb-28 sm:-m-5 sm:px-6 lg:-m-5 lg:-mb-5 lg:px-8 lg:py-5 lg:pb-5";
const SEV = {
  green: "border-emerald-400/30 bg-emerald-500/10 text-emerald-200",
  yellow: "border-amber-400/30 bg-amber-500/10 text-amber-200",
  red: "border-rose-400/30 bg-rose-500/10 text-rose-200",
  black: "border-fuchsia-400/40 bg-fuchsia-500/12 text-fuchsia-200",
};
const rp = (n) => "Rp " + (Number(n || 0)).toLocaleString("id-ID");
const fsize = (b) => (b > 1048576 ? (b / 1048576).toFixed(1) + " MB" : Math.max(1, Math.round(b / 1024)) + " KB");

const MD = {
  p: (p) => <p className="mb-2 last:mb-0 [overflow-wrap:anywhere]" {...p} />,
  ul: (p) => <ul className="mb-2 list-disc space-y-0.5 pl-4" {...p} />,
  ol: (p) => <ol className="mb-2 list-decimal space-y-0.5 pl-4" {...p} />,
  strong: (p) => <strong className="font-semibold text-white" {...p} />,
  h1: (p) => <h3 className="mb-1 mt-2 text-sm font-bold text-white" {...p} />,
  h2: (p) => <h3 className="mb-1 mt-2 text-sm font-bold text-white" {...p} />,
  h3: (p) => <h4 className="mb-1 mt-2 text-[13px] font-bold text-blue-200" {...p} />,
  table: (p) => <div className="my-2 max-w-full overflow-x-auto"><table className="w-full border-collapse text-[11px]" {...p} /></div>,
  th: (p) => <th className="border border-white/15 bg-white/5 px-2 py-1 text-left font-semibold" {...p} />,
  td: (p) => <td className="border border-white/10 px-2 py-1" {...p} />,
  code: (p) => <code className="rounded bg-black/30 px-1 py-0.5 text-[11px] [overflow-wrap:anywhere]" {...p} />,
  pre: (p) => <pre className="my-2 max-w-full overflow-x-auto rounded bg-black/30 p-2 text-[11px]" {...p} />,
  img: (p) => <img className="max-w-full rounded" alt="" {...p} />,
  a: (p) => <a className="text-blue-300 underline [overflow-wrap:anywhere]" target="_blank" rel="noreferrer" {...p} />,
};

const Stat = ({ label, value, sub, tone = "text-white" }) => (
  <div className="rounded-lg border border-white/8 bg-white/5 p-2.5">
    <div className="text-[10px] uppercase tracking-wider text-[#7E91AC]">{label}</div>
    <div className={`mt-0.5 text-sm font-bold ${tone}`}>{value}</div>
    {sub && <div className="text-[10px] text-[#8aa]">{sub}</div>}
  </div>
);
const Panel = ({ icon: Icon, title, children, testid }) => (
  <div className="ti-dcard min-w-0 p-3.5" data-testid={testid}>
    <div className="mb-2.5 flex items-center gap-2"><Icon size={15} className="text-blue-300" /><h2 className="font-heading text-xs font-bold uppercase tracking-wider text-white">{title}</h2></div>
    {children}
  </div>
);

export default function OwnerCommand() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [lastFailed, setLastFailed] = useState("");
  const [model, setModel] = useState({ provider: "anthropic", model: "claude-sonnet-5", available: {} });
  const [showModel, setShowModel] = useState(false);
  const [atts, setAtts] = useState([]);
  const [session] = useState(() => localStorage.getItem("ti_owner_ai_session") || (() => { const s = "s_" + Math.random().toString(36).slice(2); localStorage.setItem("ti_owner_ai_session", s); return s; })());
  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const camRef = useRef(null);

  const loadDash = useCallback(() => {
    setLoading(true);
    api.get("/owner/ai/dashboard").then((r) => { setData(r.data); setModel((m) => ({ ...m, provider: r.data.provider, model: r.data.model })); }).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    loadDash();
    api.get("/owner/ai/model").then((r) => setModel(r.data)).catch(() => {});
    api.get(`/owner/ai/chat/${session}`).then((r) => setMsgs(r.data || [])).catch(() => {});
    api.get(`/owner/ai/attachments`, { params: { session_id: session } }).then((r) => setAtts((r.data || []).map((a) => ({ tempId: a.id, id: a.id, name: a.filename, size: a.size, source: a.source, isPdf: a.is_pdf, status: "done", extract: a.extract_status, preview: null })))).catch(() => {});
  }, [loadDash, session]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [msgs, sending]);

  const uploadFiles = async (fileList, src) => {
    const files = Array.from(fileList).slice(0, 10 - atts.length);
    if (Array.from(fileList).length > files.length) toast.error("Maksimal 10 file per percakapan");
    for (const f of files) {
      const isPdf = f.type === "application/pdf";
      const max = isPdf ? 25 * 1048576 : 10 * 1048576;
      if (f.size > max) { toast.error(`${f.name}: ${isPdf ? "PDF maksimal 25MB" : "gambar maksimal 10MB"}`); continue; }
      const tempId = "t_" + Math.random().toString(36).slice(2);
      const preview = !isPdf && (f.type === "image/jpeg" || f.type === "image/png") ? URL.createObjectURL(f) : null;
      setAtts((a) => [...a, { tempId, name: f.name, size: f.size, source: src, status: "uploading", progress: 0, preview, isPdf }]);
      try {
        const fd = new FormData(); fd.append("file", f); fd.append("session_id", session); fd.append("source", src);
        const { data: res } = await api.post("/owner/ai/attachments", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => setAtts((a) => a.map((x) => x.tempId === tempId ? { ...x, progress: Math.round((e.loaded / (e.total || 1)) * 100) } : x)),
        });
        setAtts((a) => a.map((x) => x.tempId === tempId ? { ...x, id: res.id, status: "done", extract: res.extract_status } : x));
        if (res.extract_status !== "OK") toast.warning(`${f.name}: ekstraksi teks ${res.extract_status}`);
      } catch (err) {
        toast.error(`${f.name}: ${formatApiErrorDetail(err.response?.data?.detail) || err.message}`);
        setAtts((a) => a.filter((x) => x.tempId !== tempId));
      }
    }
  };

  const removeAtt = async (att) => {
    if (att.id) { try { await api.delete(`/owner/ai/attachments/${att.id}`); } catch (e) {} }
    if (att.preview) URL.revokeObjectURL(att.preview);
    setAtts((a) => a.filter((x) => x.tempId !== att.tempId));
  };

  const runSend = async (text) => {
    const q = (text || "").trim();
    const ids = atts.filter((a) => a.status === "done" && a.id).map((a) => a.id);
    if ((!q && ids.length === 0) || sending) return;
    const shown = q || `📎 Menganalisis ${ids.length} lampiran`;
    setInput(""); setLastFailed(""); setSending(true);
    setMsgs((m) => [...m, { role: "user", text: shown }]);
    try {
      const { data: res } = await api.post("/owner/ai/chat", { message: q || "Tolong baca, ringkas, dan analisis lampiran ini. Sebutkan sumber file/halaman.", session_id: session, attachment_ids: ids });
      setMsgs((m) => [...m, { role: "assistant", text: res.reply, model: res.model }]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLastFailed(q || "__attach__");
      setMsgs((m) => [...m, { role: "assistant", text: "⚠️ CEVAC gagal merespons. Silakan coba lagi.", error: true }]);
    } finally { setSending(false); }
  };
  const onSubmit = (e) => { e?.preventDefault(); runSend(input); };
  const onSuggest = (s) => { runSend(s); };
  const retry = () => { if (lastFailed) { setMsgs((m) => m.filter((x) => !x.error)); runSend(lastFailed === "__attach__" ? "" : lastFailed); } };

  const clearChat = async () => {
    if (!window.confirm("Hapus seluruh percakapan CEVAC beserta lampirannya?")) return;
    try {
      await api.delete(`/owner/ai/chat/${session}`);
      for (const a of atts) { if (a.id) { try { await api.delete(`/owner/ai/attachments/${a.id}`); } catch (e) {} } if (a.preview) URL.revokeObjectURL(a.preview); }
      setMsgs([]); setAtts([]); setLastFailed(""); toast.success("Percakapan dihapus");
    } catch (e) {}
  };

  const changeModel = async (provider, m) => {
    try {
      await api.post("/owner/ai/model", { provider, model: m });
      setModel((x) => ({ ...x, provider, model: m })); setShowModel(false);
      toast.success(`Model AI: ${provider}/${m}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const snap = data?.snapshot;
  const suggestions = ["Ringkas kondisi Titanium Group hari ini", "Unit mana yang tidak bergerak >24 jam?", "Tampilkan piutang overdue beserta sumbernya", "Ada anomali data yang perlu saya verifikasi?"];

  return (
    <div className={wrap} data-testid="owner-command-page">
      <div className="mx-auto w-full min-w-0 max-w-7xl">
        <div className="flex w-full min-w-0 flex-wrap items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500/12 text-blue-300 ring-1 ring-inset ring-blue-400/30" style={{ boxShadow: "0 0 16px rgba(22,132,255,0.4)" }}><Sparkles size={20} /></span>
            <div className="min-w-0"><h1 className="font-heading text-lg font-extrabold tracking-tight text-white [overflow-wrap:anywhere] sm:text-xl">CEVAC — Private Owner Command AI</h1><p className="text-[11px] text-[#AAB8CC]">ROOT OWNER Only • Read-Only · {snap?.generated_at || "…"}</p></div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <button data-testid="ai-model-btn" onClick={() => setShowModel(!showModel)} className="flex h-8 items-center gap-1.5 rounded-lg border border-[#29466D] bg-[#102544] px-2.5 text-[11px] font-semibold text-blue-200"><Cpu size={13} />{model.model}<ChevronDown size={13} /></button>
              {showModel && (
                <div className="absolute right-0 z-20 mt-1 w-56 rounded-lg border border-[#29466D] bg-[#0a2038] p-1 shadow-xl" data-testid="ai-model-menu">
                  {Object.entries(model.available || {}).map(([prov, list]) => (
                    <div key={prov} className="p-1">
                      <div className="px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-[#7E91AC]">{prov}</div>
                      {list.map((m) => (
                        <button key={m} data-testid={`ai-model-${m}`} onClick={() => changeModel(prov, m)} className={`block w-full rounded px-2 py-1 text-left text-[11px] ${model.model === m ? "bg-blue-500/20 text-blue-200" : "text-[#C3D0E2] hover:bg-white/5"}`}>{m}</button>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button data-testid="ai-refresh-btn" onClick={loadDash} className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#29466D] bg-[#102544] text-[#C3D0E2]"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /></button>
          </div>
        </div>

        <div className="mt-4 grid min-w-0 gap-3 lg:grid-cols-5">
          <div className="min-w-0 space-y-3 lg:col-span-3">
            <Panel icon={AlertTriangle} title="Alert Kritis Hari Ini" testid="cc-alerts">
              <div className="space-y-1.5">
                {(data?.alerts || []).map((a, i) => (
                  <div key={i} className={`flex items-center justify-between rounded-lg border px-2.5 py-1.5 text-[11px] ${SEV[a.sev] || SEV.green}`} data-testid={`alert-${a.sev}`}>
                    <span className="font-semibold">{a.title}</span>
                    <span className="text-right">{a.detail}<span className="ml-1.5 opacity-60">· {a.module}</span></span>
                  </div>
                ))}
              </div>
            </Panel>
            <div className="grid gap-3 sm:grid-cols-3">
              <Panel icon={Wallet} title="Cash Position" testid="cc-cash">
                <Stat label="Saldo total" value={rp(snap?.cash?.current_balance)} tone="text-emerald-300" />
                <div className="mt-2 grid grid-cols-2 gap-2"><Stat label="Kas masuk (bln)" value={rp(snap?.cash?.cash_in_month)} /><Stat label="Kas keluar (bln)" value={rp(snap?.cash?.cash_out_month)} /></div>
              </Panel>
              <Panel icon={Wallet} title="Piutang" testid="cc-ar">
                <Stat label="Total piutang" value={rp(snap?.receivable?.total)} />
                <div className="mt-2 grid grid-cols-1 gap-2"><Stat label="Overdue" value={rp(snap?.receivable?.overdue)} tone="text-rose-300" /><Stat label="Invoice menunggu" value={snap?.receivable?.pending_invoice ?? "—"} /></div>
              </Panel>
              <Panel icon={HandCoins} title="Hutang" testid="cc-ap">
                <Stat label="Total hutang" value={rp(snap?.payable?.total_payable)} />
                <div className="mt-2 grid grid-cols-1 gap-2"><Stat label="Jatuh tempo minggu ini" value={rp(snap?.payable?.due_this_week)} tone="text-amber-300" /><Stat label="Overdue" value={rp(snap?.payable?.overdue)} tone="text-rose-300" /></div>
              </Panel>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Panel icon={Factory} title="Kondisi Produksi" testid="cc-prod">
                <div className="grid grid-cols-2 gap-2"><Stat label="In workshop" value={snap?.production?.in_workshop ?? "—"} /><Stat label="Tidak bergerak >24j" value={snap?.production?.not_moving_count ?? 0} tone={snap?.production?.not_moving_count ? "text-amber-300" : "text-white"} /></div>
                <div className="mt-2 max-h-28 space-y-1 overflow-y-auto pr-1">
                  {(snap?.production?.not_moving_24h || []).map((u, i) => (
                    <div key={i} className="flex items-center justify-between rounded border border-white/8 bg-white/5 px-2 py-1 text-[10px]"><span className="font-semibold text-white">{u.plate} · WO {u.wo}</span><span className="text-[#AAB8CC]">{u.stage} · {u.hours_idle}j</span></div>
                  ))}
                </div>
              </Panel>
              <Panel icon={Gauge} title="KPI Tim (ringkas)" testid="cc-kpi">
                <div className="max-h-40 space-y-1 overflow-y-auto pr-1">
                  {(snap?.kpi_teams || []).length === 0 ? <p className="text-[11px] text-[#7E91AC]">Belum ada data KPI.</p> : (snap?.kpi_teams || []).map((t, i) => (
                    <div key={i} className="flex items-center justify-between rounded border border-white/8 bg-white/5 px-2 py-1 text-[10px]"><span className="font-bold text-blue-200">{t.team}</span><span className="text-[#AAB8CC]">{t.units} unit · {t.done}/{t.jobs} selesai · {t.overdue} overdue</span></div>
                  ))}
                </div>
              </Panel>
            </div>
            <Panel icon={AlertTriangle} title="Keputusan Menunggu & Anomali" testid="cc-pending">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <div className="mb-1 text-[10px] uppercase tracking-wider text-[#7E91AC]">Menunggu keputusan</div>
                  <div className="max-h-28 space-y-1 overflow-y-auto pr-1">
                    {(snap?.pending_decisions || []).length === 0 ? <p className="text-[11px] text-[#7E91AC]">Tidak ada.</p> : (snap?.pending_decisions || []).map((p, i) => <div key={i} className="rounded border border-white/8 bg-white/5 px-2 py-1 text-[10px] text-[#C3D0E2]">{p.type} · {p.plate} · WO {p.wo}</div>)}
                  </div>
                </div>
                <div>
                  <div className="mb-1 text-[10px] uppercase tracking-wider text-[#7E91AC]">Anomali (perlu verifikasi)</div>
                  <div className="max-h-28 space-y-1 overflow-y-auto pr-1">
                    {(snap?.anomalies || []).length === 0 ? <p className="text-[11px] text-[#7E91AC]">Tidak ada.</p> : (snap?.anomalies || []).map((p, i) => <div key={i} className="rounded border border-fuchsia-400/20 bg-fuchsia-500/10 px-2 py-1 text-[10px] text-fuchsia-200">{p.type} · {p.plate} · selisih {rp(p.difference)}</div>)}
                  </div>
                </div>
              </div>
            </Panel>
          </div>

          {/* CEVAC Chat */}
          <div className="min-w-0 lg:col-span-2">
            <div className="ti-dcard flex min-w-0 w-full max-w-full flex-col p-0 lg:h-[calc(100vh-9rem)]" data-testid="ai-chat-panel">
              <div className="flex shrink-0 items-center justify-between border-b border-[#1b3357] px-3.5 py-2.5">
                <div className="flex items-center gap-2"><span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-blue-500" /></span><h2 className="font-heading text-sm font-bold text-white">Tanyakan apa saja kepada CEVAC tentang Titanium Group</h2></div>
                <button data-testid="ai-clear-btn" onClick={clearChat} className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-[#29466D] bg-[#102544] text-[#C3D0E2]"><Trash2 size={13} /></button>
              </div>
              <div ref={scrollRef} className="min-h-[140px] max-h-[52vh] min-w-0 flex-1 space-y-3 overflow-y-auto px-3.5 py-3 lg:max-h-none">
                {msgs.length === 0 && !sending && (
                  <div className="space-y-2">
                    <p className="text-[11px] text-[#7E91AC]">Coba tanyakan kepada CEVAC (bisa lampirkan foto/PDF):</p>
                    {suggestions.map((s) => <button key={s} data-testid="ai-suggestion" onClick={() => onSuggest(s)} className="block w-full rounded-lg border border-white/8 bg-white/5 px-2.5 py-2 text-left text-[12px] text-[#C3D0E2] hover:border-blue-400/30 hover:bg-blue-500/10">{s}</button>)}
                  </div>
                )}
                {msgs.map((m, i) => (
                  <div key={i} className={`flex min-w-0 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`min-w-0 max-w-[88%] [overflow-wrap:anywhere] rounded-2xl px-3 py-2 text-[12px] leading-relaxed ${m.role === "user" ? "whitespace-pre-wrap bg-blue-600 text-white" : m.error ? "border border-rose-400/30 bg-rose-500/10 text-rose-200" : "border border-white/10 bg-white/5 text-[#E3EAF5]"}`} data-testid={`ai-msg-${m.role}`}>
                      {m.role === "assistant" && !m.error ? <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD}>{m.text}</ReactMarkdown> : m.text}
                      {m.error && <button data-testid="ai-retry-btn" onClick={retry} className="mt-2 flex items-center gap-1.5 rounded-lg border border-rose-400/40 bg-rose-500/15 px-2.5 py-1 text-[11px] font-semibold text-rose-100"><RotateCw size={12} /> Coba Lagi</button>}
                    </div>
                  </div>
                ))}
                {sending && <div className="flex justify-start"><div className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-[12px] text-[#AAB8CC]" data-testid="ai-typing"><Loader2 size={13} className="animate-spin text-blue-400" />CEVAC sedang menganalisis data…</div></div>}
              </div>

              {/* Attachment tray */}
              {atts.length > 0 && (
                <div className="flex shrink-0 gap-2 overflow-x-auto border-t border-[#1b3357] px-3 py-2" data-testid="ai-attach-tray">
                  {atts.map((a) => (
                    <div key={a.tempId} className="relative flex w-28 shrink-0 flex-col rounded-lg border border-white/10 bg-white/5 p-1.5" data-testid="ai-attach-chip">
                      <div className="flex h-14 items-center justify-center overflow-hidden rounded bg-black/30">
                        {a.preview ? <img src={a.preview} alt={a.name} className="h-full w-full object-cover" /> : <FileText size={22} className="text-blue-300" />}
                      </div>
                      <div className="mt-1 truncate text-[10px] font-semibold text-white" title={a.name}>{a.name}</div>
                      <div className="text-[9px] text-[#8aa]">{fsize(a.size)} · {a.source === "camera" ? "Diambil langsung" : "Upload dari galeri"}</div>
                      {a.status === "uploading" ? (
                        <div className="mt-1 h-1 w-full overflow-hidden rounded bg-white/10"><div className="h-full bg-blue-500 transition-all" style={{ width: `${a.progress || 0}%` }} /></div>
                      ) : (
                        <div className={`mt-0.5 text-[9px] ${a.extract === "OK" ? "text-emerald-300" : "text-amber-300"}`}>{a.extract === "OK" ? "✓ terbaca" : "teks parsial"}</div>
                      )}
                      <button data-testid="ai-attach-remove" onClick={() => removeAtt(a)} className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full border border-rose-400/40 bg-rose-500/90 text-white"><X size={11} /></button>
                    </div>
                  ))}
                </div>
              )}

              <form onSubmit={onSubmit} className="flex shrink-0 items-center gap-1.5 border-t border-[#1b3357] px-2.5 py-2.5" style={{ paddingBottom: "max(0.625rem, env(safe-area-inset-bottom))" }}>
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/heic,image/heif,application/pdf" multiple className="hidden" onChange={(e) => { uploadFiles(e.target.files, "gallery"); e.target.value = ""; }} data-testid="ai-file-input" />
                <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { uploadFiles(e.target.files, "camera"); e.target.value = ""; }} data-testid="ai-camera-input" />
                <button type="button" data-testid="ai-attach-btn" onClick={() => fileRef.current?.click()} className="flex h-11 w-10 shrink-0 items-center justify-center rounded-lg border border-[#29466D] bg-[#102544] text-[#C3D0E2] hover:text-white"><Paperclip size={17} /></button>
                <button type="button" data-testid="ai-camera-btn" onClick={() => camRef.current?.click()} className="flex h-11 w-10 shrink-0 items-center justify-center rounded-lg border border-[#29466D] bg-[#102544] text-[#C3D0E2] hover:text-white"><Camera size={17} /></button>
                <Input data-testid="ai-chat-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Tanya CEVAC…" className="h-11 border-white/12 bg-white/5 text-base text-white placeholder:text-slate-500 sm:text-sm" />
                <Button type="submit" data-testid="ai-send-btn" disabled={sending || (!input.trim() && atts.filter((a) => a.status === "done").length === 0)} className="flex h-11 shrink-0 items-center gap-1.5 bg-blue-600 px-3.5 font-semibold text-white hover:bg-blue-500 disabled:opacity-50"><Send size={16} /><span className="hidden sm:inline">Kirim</span></Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
